import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { scrollToFirstError } from '../../foundation/js/inputValidators/scrollToFirstError'
import PersonalDonationStep3 from 'components/PersonalDonationStep3/PersonalDonationStep3.vue'
import FormInputTextField from 'components/FormInputTextField/FormInputTextField.vue'
import TooltipComponent from 'components/TooltipComponent/TooltipComponent.vue'
import emailValidator from '../../foundation/js/inputValidators/emailValidator'
import phoneValidator from '../../foundation/js/inputValidators/phoneValidator'
import nameValidator from '../../foundation/js/inputValidators/nameValidator'
import onTransitionendCallback from 'components/PageOverlay/methods/onTransitionendCallback'
import cprValidator from 'foundation/js/inputValidators/cprValidator'

export default {
    name: 'PersonalDonationStep2a',
    components: {
        PersonalDonationStep3,
        FormInputTextField,
        TooltipComponent
    },
    setup() {
        let first, last, close

        // constants
        const PAYMENTMETHOD_BETALINGSSERVICE = '4'
        const PAYMENT_OPTIONS = {
            single: ['mobilepay', 'creditcard'],
            recurring: ['mobilepaysubscription', 'betalingsservice']
        }

        // dependencies
        const store = useStore()
        const getLabel = (labelKey) =>
            store.getters['labels/getLabel']({
                labelCollection: 'personalDonation',
                labelKey: labelKey
            })
        const personalDonationInput = computed(
            () => store.state.donation.personalDonationInput
        ).value
        const isSubmitting = computed(
            () => store.state.fundraising.isSubmitting
        )

        const donationType = ref(
            personalDonationInput?.donationType ?? getLabel('personalDonationStep2a_default_radio_value')
        )
        const donationSettings = computed(
            () => store.state.fundraising.donation_settings
        )
        const donationPresets = computed(() => {
            return donationType.value === 'single'
                ? donationSettings.value.single.presets
                : donationSettings.value.recurring.presets
        })
        const donationPresetDefault = computed(() => {
            return donationType.value === 'single'
                ? donationSettings.value.single.presets[
                      donationSettings.value.single.defaultPreset
                  ]
                : donationSettings.value.recurring.presets[
                      donationSettings.value.recurring.defaultPreset
                  ]
        })
        const showMobilepaySubscription = computed(
            () => donationSettings.value.recurring.showMobilepaySubscription
        )
        const showReceipt = computed(
            () => store.state.fundraising.personalDonationShowReceipt
        )
        const pageId = computed(() => store.state.main.page_id)

        const pbsFieldsVisible = ref(
            personalDonationInput?.pbsFieldsVisible ?? false
        )
        const showPBSfields = function () {
            pbsFieldsVisible.value = true
        }
        const hidePBSfields = function () {
            pbsFieldsVisible.value = false
        }

        // input bindings, validations and helpers
        const donationPresetsSelectedIndex = ref(
            personalDonationInput?.donationPresetsSelectedIndex ?? 1
        )

        const firstNameInput = ref(personalDonationInput?.firstNameInput ?? '')
        const firstNameInputFilled = computed(() => {
            return unSubmitted.value && !firstNameInput.value
                ? true
                : nameValidator(firstNameInput.value)
        })

        const lastNameInput = ref(personalDonationInput?.lastNameInput ?? '')
        const lastNameInputFilled = computed(() => {
            return unSubmitted.value && !lastNameInput.value
                ? true
                : nameValidator(lastNameInput.value)
        })

        const phoneInput = ref(personalDonationInput?.phoneInput ?? '')
        const phoneInputFilled = computed(() => {
            return unSubmitted.value ? true : phoneValidator(phoneInput.value)
        })

        const emailInput = ref(personalDonationInput?.emailInput ?? '')
        const emailInputFilled = computed(() => {
            return unSubmitted.value ? true : emailValidator(emailInput.value)
        })
        const donationAmountInput = ref(
            personalDonationInput?.donationAmountInput ??
                donationPresetDefault.value
        )
        const donationAmountInputFilled = computed(() => {
            return unSubmitted.value ? true : !!donationAmountInput.value
        })

        const customDonationAmountInput = ref(
            personalDonationInput?.customDonationAmountInput ??
                donationAmountInput.value
        )
        const customDonationAmountInputIsValid = computed(() => {
            return unSubmitted.value && !forceCustomAmountValidation.value
                ? true
                : customDonationAmountInput.value >=
                      (donationSettings.value.minimumAmount ?? 1)
        })

        const cprNumberInput = ref(personalDonationInput?.cprNumberInput ?? '')
        const cprNumberInputFilled = computed(() => {
            if (unSubmitted.value) {
                return true
            }
            return taxDeductible.value ||
                paymentMethod.value === PAYMENTMETHOD_BETALINGSSERVICE
                ? cprValidator(cprNumberInput.value)
                : true
        })

        const paymentMethod = ref(personalDonationInput?.paymentMethod ?? '')
        const paymentMethodFilled = computed(() => {
            return unSubmitted.value ? true : !!paymentMethod.value
        })

        const pbsRegInput = ref(personalDonationInput?.pbsRegInput ?? '')
        const pbsRegInputFilled = computed(() => {
            return unSubmitted.value
                ? true
                : pbsRegInput.value.toString().length === 4
        })

        const pbsAccnumInput = ref(personalDonationInput?.pbsAccnumInput ?? '')
        const pbsAccnumInputFilled = computed(() => {
            return unSubmitted.value
                ? true
                : pbsAccnumInput.value.toString().length >= 7 &&
                      pbsAccnumInput.value.toString().length <= 10
        })

        const unSubmitted = ref(true)
        const forceCustomAmountValidation = ref(false)
  
        
        const submitDonation = () => {
            unSubmitted.value = false;
        
            const isAllValid = validateAllFields(); 
            if (!isAllValid) {
                scrollToFirstError()
                return false; 
            }
        
            const donationPayload = {
                firstName: firstNameInput.value,
                lastName: lastNameInput.value,
                phone: phoneInput.value,
                email: emailInput.value,
                donationAmount: donationAmount.value,
                paymentMethod: paymentMethod.value,
                taxDeductible: taxDeductible.value,
                cprNo: cprNumberInput.value || null,
                recurringDonation: donationType.value === 'recurring',
                donationId: donationSettings.donationId,
                pbsData: pbsRegInput.value
                    ? {
                        reg: pbsRegInput.value?.toString(),
                        acc: pbsAccnumInput.value?.toString()
                      }
                    : null,
                pageId: pageId.value,
                isCustomAmount: !!customDonation.value,
                referrer: document.referrer
            };
        

            store.dispatch('fundraising/submitPersonalDonation', donationPayload)
                .then(response => {
                    if (response.success) {
                        console.log('Donation submitted successfully');
                    } else {
                        console.log('Donation submission failed');
                    }
                });
        };
        
        const validateAllFields = () => {
            const isValid =
                paymentMethodFilled.value &&
                (customDonation.value
                    ? customDonationAmountInputIsValid.value
                    : true) &&
                emailInputFilled.value &&
                phoneInputFilled.value &&
                lastNameInputFilled.value &&
                firstNameInputFilled.value &&
                cprNumberInputFilled.value &&
                paymentMethod.value &&
                (paymentMethod.value === PAYMENTMETHOD_BETALINGSSERVICE
                    ? pbsRegInputFilled.value &&
                      pbsAccnumInputFilled.value &&
                      cprNumberInputFilled.value
                    : true);
        
            // Check for validation on each field individually
            // You can add more fields as needed here
            if (!isValid) {
                return false;
            }
        
            return true;
        };
        

        const customDonation = ref(
            personalDonationInput?.customDonation ?? false
        )
        const valueinputfield = ref(null)

        const enableCustomDonation = async function () {
            forceCustomAmountValidation.value = true
            customDonation.value = true
            donationAmountInput.value = ''
            await nextTick() // we need to wait for DOM to update
            valueinputfield.value.focus()
        }

        const disableCustomDonation = function () {
            customDonation.value = false
            forceCustomAmountValidation.value = false
        }

        const taxDeductible = ref(personalDonationInput?.taxDeductible ?? false)

        const donationAmount = computed(() => {
            return customDonation.value
                ? customDonationAmountInput.value
                : donationAmountInput.value
        })
        const paymentButtonTextAddition = computed(
            () => donationAmount.value + ' kr '
        )

        const tabTrap = function (e) {
            if (e.shiftKey && e.target.dataset.order === 'first') {
                e.preventDefault()
                close.focus()
            }

            if (!e.shiftKey && e.target.dataset.order === 'last') {
                e.preventDefault()
                close.focus()
            }

            if (!e.shiftKey && !e.target.dataset.order) {
                e.preventDefault()
                first.focus()
            }
        }

        const resetBetalingsServiceFields = () => {
            paymentMethod.value = ''
            pbsAccnumInput.value = ''
            pbsRegInput.value = ''
        }

        const donationAmountInputChangeHandler = () => {
            donationPresetsSelectedIndex.value =
                donationType.value === 'single'
                    ? donationSettings.value.single.presets.indexOf(
                          donationAmountInput.value
                      )
                    : donationSettings.value.recurring.presets.indexOf(
                          donationAmountInput.value
                      )
        }

        const donationTypeChangeHandler = () => {
            if (customDonation.value) {
                return
            }

            let presetSingleIndexAmount =
                donationSettings.value.single.presets[
                    donationPresetsSelectedIndex.value
                ]
            let presetRecurringIndexAmount =
                donationSettings.value.recurring.presets[
                    donationPresetsSelectedIndex.value
                ]
            // if presets with same index have different values, we activate it as custom donation amount
            if (presetSingleIndexAmount != presetRecurringIndexAmount) {
                customDonationAmountInput.value = donationAmountInput.value
                enableCustomDonation() // deselect preset
                return
            }
            donationAmountInput.value =
                donationType.value === 'single'
                    ? presetSingleIndexAmount
                    : presetRecurringIndexAmount
        }

        watch(donationAmountInput, () => {
            donationAmountInputChangeHandler()
        })

        watch(donationType, () => {
            resetBetalingsServiceFields()
            donationTypeChangeHandler()
        })

        onMounted(() => {
            first = document.querySelectorAll(
                '.kb-personal-donation-step2a__donation-input [data-order="first"]'
            )[donationPresetsSelectedIndex.value ?? 1]
            last = document.querySelector(
                '.kb-personal-donation-step2a [data-order="last"]'
            )
            close = document.querySelector('.kb-page-overlay__close')
            onTransitionendCallback(
                {
                    opening() {
                        if (first) first.focus()
                    }
                },
                { persistent: true }
            )
        })

        onBeforeUnmount(() => {
            let input = {
                pbsFieldsVisible: pbsFieldsVisible.value,
                firstNameInput: firstNameInput.value,
                lastNameInput: lastNameInput.value,
                phoneInput: phoneInput.value,
                emailInput: emailInput.value,
                donationAmountInput: donationAmountInput.value,
                customDonationAmountInput: customDonationAmountInput.value,
                cprNumberInput: cprNumberInput.value,
                paymentMethod: paymentMethod.value,
                pbsRegInput: pbsRegInput.value,
                pbsAccnumInput: pbsAccnumInput.value,
                customDonation: customDonation.value,
                donationType: donationType.value,
                taxDeductible: taxDeductible.value,
                donationPresetsSelectedIndex: donationPresetsSelectedIndex.value
            }
            store.commit('donation/store_personal_donation_input', input)
        })

        return {
            // inpuut fields and properties
            cprNumberInput,
            cprNumberInputFilled,
            customDonation,
            customDonationAmountInput,
            customDonationAmountInputIsValid,
            donationAmountInput,
            donationAmountInputFilled,
            donationPresets,
            donationType,
            emailInput,
            emailInputFilled,
            firstNameInput,
            firstNameInputFilled,
            isSubmitting,
            lastNameInput,
            lastNameInputFilled,
            paymentButtonTextAddition,
            paymentMethod,
            paymentMethodFilled,
            paymentOptions: PAYMENT_OPTIONS,
            pbsAccnumInput,
            pbsAccnumInputFilled,
            pbsFieldsVisible,
            pbsRegInput,
            pbsRegInputFilled,
            phoneInput,
            phoneInputFilled,
            showReceipt,
            taxDeductible,
            valueinputfield,
            showMobilepaySubscription,

            // methods
            disableCustomDonation,
            enableCustomDonation,
            getLabel,
            hidePBSfields,
            showPBSfields,
            submitDonation,
            tabTrap
        }
    }
}
